import { useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, ArrowRight } from 'lucide-react';
import { useTranslation } from '@/hooks/useTranslation';
import { usePageMeta } from '@/hooks/usePageMeta';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import CustomCursor from '@/components/CustomCursor';
import { BASE_URL, CX_SERVICE_SLUGS, cxIcon, isCxServiceSlug } from './cxData';

const CxServicePage = () => {
  const { slug } = useParams<{ slug: string }>();
  const { t, language } = useTranslation();
  const cx = t.cx;
  const valid = isCxServiceSlug(slug);
  const page = valid ? cx.servicePages[slug] : undefined;
  const index = valid ? CX_SERVICE_SLUGS.indexOf(slug) : -1;
  const summary = index >= 0 ? cx.services.items[index] : undefined;

  usePageMeta({
    title: page ? `${page.title} — ADD Individual Solutions` : cx.meta.hubTitle,
    description: page ? page.metaDescription : cx.meta.hubDescription,
    canonical: valid ? `${BASE_URL}/cx/services/${slug}` : `${BASE_URL}/cx`,
  });

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  if (!page || !summary || !valid) {
    return (
      <>
        <Navbar />
        <main className="min-h-screen flex items-center justify-center" style={{ paddingTop: 120 }}>
          <div className="text-center">
            <h1 className="text-3xl font-bold mb-4" style={{ fontFamily: 'var(--f-display)', color: 'var(--ink)' }}>Service not found</h1>
            <Link to="/cx" className="text-[var(--amber)] font-semibold hover:underline">← {cx.breadcrumbs.cx}</Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const Icon = cxIcon(summary.icon);
  const others = CX_SERVICE_SLUGS.filter((s) => s !== slug);

  const schema = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'Service',
        '@id': `${BASE_URL}/cx/services/${slug}#service`,
        serviceType: page.title,
        name: page.title,
        description: page.metaDescription,
        url: `${BASE_URL}/cx/services/${slug}`,
        provider: { '@id': `${BASE_URL}/#organization` },
        areaServed: [
          { '@type': 'Country', name: 'Germany' },
          { '@type': 'Country', name: 'Austria' },
          { '@type': 'Country', name: 'Switzerland' },
          { '@type': 'Place', name: 'European Union' },
        ],
        availableLanguage: ['de', 'en', 'ro'],
        inLanguage: language,
      },
      {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: cx.breadcrumbs.home, item: `${BASE_URL}/` },
          { '@type': 'ListItem', position: 2, name: cx.breadcrumbs.cx, item: `${BASE_URL}/cx` },
          { '@type': 'ListItem', position: 3, name: page.title, item: `${BASE_URL}/cx/services/${slug}` },
        ],
      },
    ],
  };

  return (
    <>
      <CustomCursor />
      <Navbar />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }} />

      <main style={{ paddingTop: 100 }}>
        {/* Breadcrumb */}
        <div className="max-w-6xl mx-auto px-6 mb-6">
          <nav className="flex items-center gap-2 text-sm" style={{ color: 'var(--slate-light)', fontFamily: 'var(--f-mono)' }}>
            <Link to="/" className="hover:text-[var(--amber)] transition-colors">{cx.breadcrumbs.home}</Link>
            <span>/</span>
            <Link to="/cx" className="hover:text-[var(--amber)] transition-colors">{cx.breadcrumbs.cx}</Link>
            <span>/</span>
            <span style={{ color: 'var(--ink)' }}>{page.title}</span>
          </nav>
        </div>

        {/* Hero */}
        <section className="max-w-6xl mx-auto px-6 pb-16">
          <div className="flex items-center gap-4 mb-6">
            <div className="w-14 h-14 rounded-2xl flex items-center justify-center" style={{ background: 'var(--blue)', color: 'var(--white)' }}>
              <Icon size={28} />
            </div>
            <div className="flex flex-wrap gap-2">
              {summary.tags.map((tag) => (
                <span key={tag} className="px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider" style={{ background: 'var(--blue-light)', color: 'var(--blue)', fontFamily: 'var(--f-mono)' }}>{tag}</span>
              ))}
            </div>
          </div>
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold leading-tight mb-6" style={{ fontFamily: 'var(--f-display)', color: 'var(--ink)' }}>{page.title}</h1>
          <p className="text-lg md:text-xl leading-relaxed max-w-3xl" style={{ color: 'var(--slate)' }}>{page.heroDescription}</p>
        </section>

        {/* Detail sections */}
        <section className="max-w-6xl mx-auto px-6 pb-20">
          <div className="grid md:grid-cols-2 gap-8">
            {page.sections.map((sec, i) => (
              <div key={i} className="p-8 rounded-2xl" style={{ background: i % 2 === 0 ? 'var(--off-white)' : 'var(--white)', border: '1px solid var(--line)' }}>
                <h2 className="text-2xl font-bold mb-4" style={{ fontFamily: 'var(--f-display)', color: 'var(--blue)' }}>{sec.heading}</h2>
                <p className="leading-relaxed" style={{ color: 'var(--slate)', lineHeight: 1.8 }}>{sec.content}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Other service lines */}
        <section className="max-w-6xl mx-auto px-6 pb-20">
          <h2 className="text-2xl font-bold mb-6" style={{ fontFamily: 'var(--f-display)', color: 'var(--ink)' }}>{cx.services.title}</h2>
          <div className="grid sm:grid-cols-3 gap-5">
            {others.map((s) => {
              const idx = CX_SERVICE_SLUGS.indexOf(s);
              const item = cx.services.items[idx];
              const OIcon = cxIcon(item.icon);
              return (
                <Link key={s} to={`/cx/services/${s}`} className="p-6 rounded-2xl transition-transform hover:-translate-y-0.5" style={{ background: 'var(--white)', border: '1px solid var(--line)' }}>
                  <div className="w-10 h-10 rounded-full flex items-center justify-center mb-4" style={{ background: 'var(--blue)', color: 'var(--white)' }}><OIcon size={18} /></div>
                  <div className="font-bold mb-2" style={{ fontFamily: 'var(--f-display)', color: 'var(--ink)' }}>{item.title}</div>
                  <div className="text-sm leading-relaxed" style={{ color: 'var(--slate)' }}>{item.summary}</div>
                </Link>
              );
            })}
          </div>
        </section>

        {/* CTA */}
        <section className="max-w-6xl mx-auto px-6 pb-24">
          <div className="rounded-2xl p-10 md:p-14" style={{ background: 'var(--blue)', color: 'var(--white)' }}>
            <h2 className="text-3xl md:text-4xl font-bold mb-4" style={{ fontFamily: 'var(--f-display)' }}>{cx.cta.title}</h2>
            <p className="text-lg mb-8 max-w-3xl" style={{ color: 'rgba(255,255,255,0.82)' }}>{cx.cta.subtitle}</p>
            <div className="flex flex-wrap gap-4">
              <Link to="/cx/assessment" className="inline-flex items-center gap-2 px-8 py-4 rounded-full font-bold text-base transition-all hover:scale-105" style={{ background: 'var(--amber)', color: 'var(--ink)' }}>
                {cx.cta.button} <ArrowRight size={18} />
              </Link>
              <Link to="/cx" className="inline-flex items-center gap-2 px-8 py-4 rounded-full font-bold text-base transition-all hover:scale-105" style={{ background: 'rgba(255,255,255,0.15)', color: 'var(--white)' }}>
                <ArrowLeft size={18} /> {cx.breadcrumbs.cx}
              </Link>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
};

export default CxServicePage;
